import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import moment from "moment-timezone";

import { expectedArrivals, valetIntakeCandidates } from "./queries";
import {mapBookingToArrival} from "../helpers";
import {REFRESH_INTERVAL_MS} from "../constants";

export const PAGE_SIZE = 20;

export default function useExpectedArrivals({
  search,
  isValet = true,
  includeReturns = false,
} = {}) {
  const [limit, setLimit] = useState(PAGE_SIZE);

  const startDayFrom = useMemo(
    () => moment().startOf("day").toISOString(),
    [],
  );

  // Empty string would key a second, identical cache entry, so drop it.
  const searchArg = search?.trim() ? search.trim() : undefined;

  const { data, loading, error } = useQuery(
    includeReturns ? valetIntakeCandidates : expectedArrivals,
    {
      variables: {
        offset: 0,
        limit,
        search: searchArg,
        isValet: isValet ?? undefined,
        // Right for arrivals; it would hide a stay that began days ago.
        ...(includeReturns ? {} : { startDayFrom }),
      },
      fetchPolicy: "cache-and-network",
      pollInterval: REFRESH_INTERVAL_MS,
    },
  );

  const rawItems = data?.listBookings?.items ?? [];
  const count = data?.listBookings?.count ?? 0;

  const items = useMemo(
    () => rawItems.filter(Boolean).map(mapBookingToArrival),
    [rawItems],
  );

  const loadMore = useCallback(() => setLimit((l) => l + PAGE_SIZE), []);

  return {
    items,
    count,
    // Keep the rows on screen while a poll or a wider page is in flight.
    loading: loading && rawItems.length === 0,
    loadingMore: loading && rawItems.length > 0 && rawItems.length < limit,
    error,
    hasMore: items.length < count,
    loadMore,
  };
}
